import { isObject, isEqual } from 'lodash'
import { createActor, StateValueMap } from 'xstate'

import { FSMStorageKey, FSM_PRESERVED_DATA_KEY, FSMCommonTransitions } from './shared/constants'
import { FlowMachineType, FSMStateType } from '../types/interface/fsm.interface'
import { getProductName } from '@dg-util'
import appHistory from '../routes/history'
import { OFFER_VERIFY, PAYMENT_CALLBACK_PATH_REGEXP } from '../constants/route-constants'

// External callbacks must keep their raw URL on first app boot so the callback pages
// can read payment status / BankID params before FSM history sync rewrites the path.
// The payment pattern is shared with the routes in `App.tsx` — see route-constants.
const AUTH_BANK_ID_CALLBACK_PATH_REGEXP =
  /^\/auth-bank-id\/(success|failure|error)\/[^/]+\/(in|out)$/

const removePersistedState = (): void => localStorage.removeItem(FSMStorageKey)

const safeParse = (raw: string | null) => {
  if (!raw) return null

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// The event type that produced the persisted state. v4 serialized `state.event` into the blob,
// so this survived a reload; keeping it persisted preserves that.
const getPersistedLastEventType = (): string | undefined =>
  safeParse(localStorage.getItem(FSMStorageKey))?.lastEventType

const getInitialSnapshot = (machine: FlowMachineType): FSMStateType =>
  createActor(machine).getSnapshot() as FSMStateType

const getPersistedState = (machine: FlowMachineType): FSMStateType | undefined => {
  // `switchFSM` stores a one-shot state override here when we jump between flows/products
  // and need to reopen the target machine at a specific state with preserved context.
  const fsm_preserved_data = localStorage.getItem(FSM_PRESERVED_DATA_KEY)

  if (fsm_preserved_data) {
    const fsm_preserved = safeParse(fsm_preserved_data)

    localStorage.removeItem(FSM_PRESERVED_DATA_KEY)

    if (fsm_preserved) {
      const preservedState = machine.resolveState({
        value: fsm_preserved.value,
        context: fsm_preserved.context,
      }) as FSMStateType

      persistState(preservedState)

      return preservedState
    }
  }

  const stateFetched = safeParse(localStorage.getItem(FSMStorageKey))

  if (!stateFetched) return getInitialSnapshot(machine)

  const isEqualProductName = getProductName() === stateFetched.productName

  if (!isEqualProductName) {
    // Product-specific FSM state is not portable. Drop stale cache instead of restoring,
    // for example, a vehicle flow state into another product.
    removePersistedState()
    return
  }

  try {
    return machine.resolveState({
      value: stateFetched.value,
      context: stateFetched.context,
    }) as FSMStateType
  } catch {
    // A payload this machine cannot resolve (shape from an older release, a truncated write)
    // used to throw here and take the whole app down on boot. Drop it and start fresh
    // instead — losing a resumed flow is recoverable, a white screen is not.
    removePersistedState()
    return getInitialSnapshot(machine)
  }
}

const persistState = (stateFSM: FSMStateType, lastEventType?: string) => {
  const productName = getProductName()
  // Only value + context are restorable, so persist just those instead of the whole snapshot.
  // v4 spread the entire State object here — ~4 KB of machine internals per write.
  const jsonState = JSON.stringify({
    value: stateFSM.value,
    context: stateFSM.context,
    productName,
    lastEventType,
  })

  try {
    localStorage.setItem(FSMStorageKey, jsonState)
  } catch (e) {
    throw new Error('Inability to save the state')
  }
}

const getObjectValue = (value: StateValueMap | string): string => {
  // Nested XState values like { purchased: 'mileage' } need their leaf state name.
  return isObject(value) ? getObjectValue(value[Object.keys(value)[0]]) : value
}

const getClientPath = (value: string | StateValueMap): string => {
  // FSM state keys are not always browser routes. Normalize them to the URLs this app owns.
  if (!isObject(value)) {
    if (value === FSMCommonTransitions.QB_VERIFY) {
      return OFFER_VERIFY
    }

    return `/${value}`
  }

  const outerKey = Object.keys(value)[0]
  const innerValue = getObjectValue(value)

  if (outerKey === FSMCommonTransitions.QB_PURCHASED) {
    // QB purchased screens live under /accept/*, not under the raw state key.
    return `/accept/${innerValue}`
  }

  if (outerKey === FSMCommonTransitions.POLICIES) {
    // My Pages policy list is the app root; the other policy screens keep flat paths.
    return innerValue === FSMCommonTransitions.POLICIES_LIST ? '/' : `/${innerValue}`
  }

  if (outerKey === FSMCommonTransitions.AUTH_BANK_ID) {
    return `/${FSMCommonTransitions.AUTH}`
  }

  return `/${innerValue}`
}

const shouldSkipHistorySync = (pathname: string, hasStateChanged?: boolean): boolean =>
  // The initial subscription emission is the one moment where we must not steal an external
  // callback URL before the callback page consumes it. On xstate v4 that moment was identified
  // by `state.changed === undefined`; v5 snapshots have no `changed`, so `FSMService` passes
  // `undefined` for the first emission and a boolean afterwards. Signature is unchanged.
  (PAYMENT_CALLBACK_PATH_REGEXP.test(pathname) ||
    AUTH_BANK_ID_CALLBACK_PATH_REGEXP.test(pathname)) &&
  hasStateChanged === undefined

const isCallbackPath = (pathname: string): boolean =>
  PAYMENT_CALLBACK_PATH_REGEXP.test(pathname) || AUTH_BANK_ID_CALLBACK_PATH_REGEXP.test(pathname)

const setClientHistory = (value: string | StateValueMap): void => {
  const clientPath = getClientPath(value)

  // Push through the shared router history so React Router sees the navigation as well.
  // Skip duplicate pushes when browser back/forward has already restored the same FSM state.
  if (isEqual(appHistory.location.state, value)) return

  // Leaving an external callback URL *replaces* it. A callback URL is a one-shot redirect target,
  // and leaving it in the history stack lets browser Back re-enter `/payment/<status>`, which
  // remounts PaymentHandlingPage and re-runs `updatePolicyPaymentOnSuccess`. react-router 5 made
  // that unreachable by accident — its `useLocation()` was broken, so no route could re-match after
  // an FSM navigation — so this was invisible until v7 restored live location updates.
  if (isCallbackPath(appHistory.location.pathname)) {
    appHistory.replace(clientPath, value)
    return
  }

  appHistory.push(clientPath, value)
}

export {
  getInitialSnapshot,
  getPersistedLastEventType,
  getPersistedState,
  removePersistedState,
  persistState,
  setClientHistory,
  shouldSkipHistorySync,
}
